import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Plus, 
  Calendar,
  Clock,
  Users,
  Trash2,
  Edit2,
  Eye,
  Loader2,
  Download,
  Search,
  Filter,
  Video,
  Phone,
  UserCheck,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Save,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const MEETING_MODES = [
  { value: 'in_person', label: 'In-Person', icon: Users },
  { value: 'video', label: 'Video Call', icon: Video },
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'virtual', label: 'Virtual', icon: Video }
];

const MEETING_TYPES = [
  { value: 'staff', label: 'Staff Meeting' },
  { value: 'parent', label: 'Parent Meeting' },
  { value: 'clinical', label: 'Clinical Review' },
  { value: 'team', label: 'Team Meeting' },
  { value: 'supervision', label: 'Supervision' },
  { value: 'training', label: 'Training' },
  { value: 'board', label: 'Board Meeting' },
  { value: 'external', label: 'External Meeting' },
  { value: 'other', label: 'Other' }
];

export default function MeetingRecordsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMode, setFilterMode] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  
  const [newRecord, setNewRecord] = useState({
    title: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '10:00',
    meeting_mode: 'in_person',
    meeting_type: 'staff',
    location: '',
    attendees_text: '',
    agenda: '',
    discussion_notes: '',
    decisions: '',
    action_items: [
      { sr_no: 1, discussed_topic: '', responsible_person: '', action_taken: '', yet_to_take: '' }
    ],
    next_meeting_date: '',
    follow_up_notes: ''
  });

  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderRecord, setReminderRecord] = useState(null);
  const [reminderMinutes, setReminderMinutes] = useState('30');

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [records, searchTerm, filterType, filterMode]);

  const fetchData = async () => {
    try {
      const [recordsRes, staffRes] = await Promise.all([
        api.get('/meetings/records/list'),
        api.get('/staff')
      ]);
      setRecords(recordsRes.data.records || []);
      setStaff(staffRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRecords = () => {
    let filtered = [...records];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(term) ||
        r.attendees_text?.toLowerCase().includes(term) ||
        r.discussion_notes?.toLowerCase().includes(term)
      );
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(r => r.meeting_type === filterType);
    }
    
    if (filterMode !== 'all') {
      filtered = filtered.filter(r => r.meeting_mode === filterMode);
    }
    
    setFilteredRecords(filtered);
  };

  const addActionItem = () => {
    setNewRecord({
      ...newRecord,
      action_items: [
        ...newRecord.action_items,
        { sr_no: newRecord.action_items.length + 1, discussed_topic: '', responsible_person: '', action_taken: '', yet_to_take: '' }
      ]
    });
  };

  const updateActionItem = (index, field, value) => {
    const updated = [...newRecord.action_items];
    updated[index][field] = value;
    setNewRecord({ ...newRecord, action_items: updated });
  };

  const removeActionItem = (index) => {
    if (newRecord.action_items.length <= 1) return;
    const updated = newRecord.action_items.filter((_, i) => i !== index);
    // Renumber
    updated.forEach((item, i) => item.sr_no = i + 1);
    setNewRecord({ ...newRecord, action_items: updated });
  };

  const saveRecord = async () => {
    if (!newRecord.title.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }

    setSaving(true);
    try {
      if (showEditDialog && selectedRecord) {
        await api.put(`/meetings/records/${selectedRecord.id}`, newRecord);
        toast.success('Meeting record updated!');
      } else {
        await api.post('/meetings/records/create', newRecord);
        toast.success('Meeting record created!');
      }
      
      setShowAddDialog(false);
      setShowEditDialog(false);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setNewRecord({
      title: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '10:00',
      meeting_mode: 'in_person',
      meeting_type: 'staff',
      location: '',
      attendees_text: '',
      agenda: '',
      discussion_notes: '',
      decisions: '',
      action_items: [
        { sr_no: 1, discussed_topic: '', responsible_person: '', action_taken: '', yet_to_take: '' }
      ],
      next_meeting_date: '',
      follow_up_notes: ''
    });
  };

  const openEditDialog = (record) => {
    setSelectedRecord(record);
    setNewRecord({
      title: record.title || '',
      date: record.date || format(new Date(), 'yyyy-MM-dd'),
      start_time: record.start_time || '09:00',
      end_time: record.end_time || '10:00',
      meeting_mode: record.meeting_mode || 'in_person',
      meeting_type: record.meeting_type || 'staff',
      location: record.location || '',
      attendees_text: record.attendees_text || '',
      agenda: record.agenda || '',
      discussion_notes: record.discussion_notes || '',
      decisions: record.decisions || '',
      action_items: record.action_items?.length > 0 ? record.action_items : [
        { sr_no: 1, discussed_topic: '', responsible_person: '', action_taken: '', yet_to_take: '' }
      ],
      next_meeting_date: record.next_meeting_date || '',
      follow_up_notes: record.follow_up_notes || ''
    });
    setShowEditDialog(true);
  };

  const viewRecord = (record) => {
    setSelectedRecord(record);
    setShowViewDialog(true);
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Delete this meeting record?')) return;
    
    try {
      await api.delete(`/meetings/records/${id}`);
      toast.success('Record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const openReminderDialog = (record) => {
    setReminderRecord(record);
    setReminderMinutes('30');
    setShowReminderDialog(true);
  };

  const setReminder = async () => {
    if (!reminderRecord) return;
    
    try {
      await api.post(`/meetings/records/${reminderRecord.id}/set-reminder`, {
        reminder_time: reminderMinutes,
        notify_via: 'email'
      });
      toast.success(`Reminder set for ${reminderMinutes} minutes before the meeting`);
      setShowReminderDialog(false);
      setReminderRecord(null);
    } catch (error) {
      toast.error('Failed to set reminder');
    }
  };

  const exportRecords = () => {
    const csv = [
      ['Date', 'Title', 'Type', 'Mode', 'Location', 'Attendees', 'Decisions', 'Created By'],
      ...filteredRecords.map(r => [
        r.date,
        r.title,
        r.meeting_type,
        r.meeting_mode,
        r.location || '',
        r.attendees_text || '',
        r.decisions || '',
        r.created_by_name || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting_records_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getModeIcon = (mode) => {
    const ModeIcon = MEETING_MODES.find(m => m.value === mode)?.icon || Users;
    return <ModeIcon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#2a9d8f]" />
            Meeting Records
          </h1>
          <p className="text-slate-500 mt-1">Manual entry for all meetings with notes and action items</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportRecords}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90"
            data-testid="add-record-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Meeting Record
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-icrcl">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search meetings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {MEETING_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                {MEETING_MODES.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Badge variant="outline" className="text-sm">
              {filteredRecords.length} records
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card className="card-icrcl">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1e3a5f] text-white">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Title</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-left">Attendees</th>
                  <th className="p-3 text-left">Action Items</th>
                  <th className="p-3 text-left">Created By</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No meeting records found</p>
                      <Button 
                        onClick={() => { resetForm(); setShowAddDialog(true); }}
                        className="mt-4 bg-[#1e3a5f]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Record
                      </Button>
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record, i) => (
                    <tr key={record.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {record.date}
                        </div>
                        <span className="text-xs text-slate-500">
                          {record.start_time} - {record.end_time}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="font-medium text-[#1e3a5f]">{record.title}</span>
                        {record.location && (
                          <p className="text-xs text-slate-500">{record.location}</p>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">{record.meeting_type}</Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {getModeIcon(record.meeting_mode)}
                          <span className="text-sm">{record.meeting_mode?.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-slate-600 line-clamp-2">
                          {record.attendees_text || '-'}
                        </span>
                      </td>
                      <td className="p-3">
                        <Badge className={record.action_items?.length > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}>
                          {record.action_items?.length || 0} items
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">
                        {record.created_by_name}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => viewRecord(record)}
                            title="View details"
                          >
                            <Eye className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(record)}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-amber-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openReminderDialog(record)}
                            title="Set reminder"
                          >
                            <Bell className="w-4 h-4 text-purple-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteRecord(record.id)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#2a9d8f]" />
              {showEditDialog ? 'Edit Meeting Record' : 'Add Meeting Record'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Meeting Title *</Label>
                <Input
                  value={newRecord.title}
                  onChange={(e) => setNewRecord({ ...newRecord, title: e.target.value })}
                  placeholder="e.g., Weekly Team Standup, Parent Review - John Smith"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newRecord.date}
                  onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={newRecord.start_time}
                    onChange={(e) => setNewRecord({ ...newRecord, start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={newRecord.end_time}
                    onChange={(e) => setNewRecord({ ...newRecord, end_time: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Meeting Mode</Label>
                <Select
                  value={newRecord.meeting_mode}
                  onValueChange={(v) => setNewRecord({ ...newRecord, meeting_mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEETING_MODES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <Select
                  value={newRecord.meeting_type}
                  onValueChange={(v) => setNewRecord({ ...newRecord, meeting_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEETING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label>Location / Venue</Label>
                <Input
                  value={newRecord.location}
                  onChange={(e) => setNewRecord({ ...newRecord, location: e.target.value })}
                  placeholder="e.g., Conference Room A, Zoom, Phone"
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label>Attendees</Label>
                <Textarea
                  value={newRecord.attendees_text}
                  onChange={(e) => setNewRecord({ ...newRecord, attendees_text: e.target.value })}
                  placeholder="List of attendees (one per line or comma-separated)"
                  rows={2}
                />
              </div>
            </div>

            {/* Meeting Content */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-[#1e3a5f]">Meeting Content</h3>
              
              <div className="space-y-2">
                <Label>Agenda</Label>
                <Textarea
                  value={newRecord.agenda}
                  onChange={(e) => setNewRecord({ ...newRecord, agenda: e.target.value })}
                  placeholder="Meeting agenda points..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Discussion Notes</Label>
                <Textarea
                  value={newRecord.discussion_notes}
                  onChange={(e) => setNewRecord({ ...newRecord, discussion_notes: e.target.value })}
                  placeholder="Key discussion points and notes from the meeting..."
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Decisions Made</Label>
                <Textarea
                  value={newRecord.decisions}
                  onChange={(e) => setNewRecord({ ...newRecord, decisions: e.target.value })}
                  placeholder="List of decisions made during the meeting..."
                  rows={2}
                />
              </div>
            </div>

            {/* Action Items Table */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#1e3a5f]">Action Items Table</h3>
                <Button variant="outline" size="sm" onClick={addActionItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
              </div>
              
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left w-16">Sr No</th>
                      <th className="p-2 text-left">Discussed Topics</th>
                      <th className="p-2 text-left">Responsible Person</th>
                      <th className="p-2 text-left">Action Taken</th>
                      <th className="p-2 text-left">Yet to Take</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newRecord.action_items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2 text-center font-medium">{item.sr_no}</td>
                        <td className="p-2">
                          <Input
                            value={item.discussed_topic}
                            onChange={(e) => updateActionItem(index, 'discussed_topic', e.target.value)}
                            placeholder="Topic..."
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={item.responsible_person}
                            onChange={(e) => updateActionItem(index, 'responsible_person', e.target.value)}
                            placeholder="Name..."
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={item.action_taken}
                            onChange={(e) => updateActionItem(index, 'action_taken', e.target.value)}
                            placeholder="Done..."
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={item.yet_to_take}
                            onChange={(e) => updateActionItem(index, 'yet_to_take', e.target.value)}
                            placeholder="Pending..."
                            className="h-8"
                          />
                        </td>
                        <td className="p-2">
                          {newRecord.action_items.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeActionItem(index)}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Follow-up */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-[#1e3a5f]">Follow-up</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Next Meeting Date</Label>
                  <Input
                    type="date"
                    value={newRecord.next_meeting_date}
                    onChange={(e) => setNewRecord({ ...newRecord, next_meeting_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Follow-up Notes</Label>
                  <Textarea
                    value={newRecord.follow_up_notes}
                    onChange={(e) => setNewRecord({ ...newRecord, follow_up_notes: e.target.value })}
                    placeholder="Notes for follow-up actions..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}>
              Cancel
            </Button>
            <Button onClick={saveRecord} disabled={saving} className="bg-[#1e3a5f]">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Record
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#2a9d8f]" />
              Meeting Record Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f] text-white p-6 rounded-lg">
                <h2 className="text-2xl font-bold">{selectedRecord.title}</h2>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-white/80">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {selectedRecord.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedRecord.start_time} - {selectedRecord.end_time}
                  </span>
                  <Badge className="bg-white/20 text-white">{selectedRecord.meeting_type}</Badge>
                  <Badge className="bg-white/20 text-white">{selectedRecord.meeting_mode}</Badge>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedRecord.location && (
                  <div>
                    <Label className="text-slate-500">Location</Label>
                    <p>{selectedRecord.location}</p>
                  </div>
                )}
                {selectedRecord.attendees_text && (
                  <div className="md:col-span-2">
                    <Label className="text-slate-500">Attendees</Label>
                    <p>{selectedRecord.attendees_text}</p>
                  </div>
                )}
              </div>

              {/* Content Sections */}
              {selectedRecord.agenda && (
                <div>
                  <Label className="text-slate-500">Agenda</Label>
                  <p className="whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{selectedRecord.agenda}</p>
                </div>
              )}

              {selectedRecord.discussion_notes && (
                <div>
                  <Label className="text-slate-500">Discussion Notes</Label>
                  <p className="whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{selectedRecord.discussion_notes}</p>
                </div>
              )}

              {selectedRecord.decisions && (
                <div>
                  <Label className="text-slate-500">Decisions Made</Label>
                  <p className="whitespace-pre-wrap bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    {selectedRecord.decisions}
                  </p>
                </div>
              )}

              {/* Action Items Table */}
              {selectedRecord.action_items?.length > 0 && (
                <div>
                  <Label className="text-slate-500 mb-2 block">Action Items</Label>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1e3a5f] text-white">
                        <tr>
                          <th className="p-3 text-left">Sr No</th>
                          <th className="p-3 text-left">Discussed Topics</th>
                          <th className="p-3 text-left">Responsible Person</th>
                          <th className="p-3 text-left">Action Taken</th>
                          <th className="p-3 text-left">Yet to Take</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRecord.action_items.map((item, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-3 font-medium">{item.sr_no}</td>
                            <td className="p-3">{item.discussed_topic || '-'}</td>
                            <td className="p-3">
                              <Badge variant="outline">{item.responsible_person || '-'}</Badge>
                            </td>
                            <td className="p-3">
                              {item.action_taken ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {item.action_taken}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="p-3">
                              {item.yet_to_take ? (
                                <span className="flex items-center gap-1 text-amber-600">
                                  <AlertCircle className="w-4 h-4" />
                                  {item.yet_to_take}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Follow-up */}
              {(selectedRecord.next_meeting_date || selectedRecord.follow_up_notes) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-700 mb-2">Follow-up</h4>
                  {selectedRecord.next_meeting_date && (
                    <p className="text-sm">
                      <strong>Next Meeting:</strong> {selectedRecord.next_meeting_date}
                    </p>
                  )}
                  {selectedRecord.follow_up_notes && (
                    <p className="text-sm mt-1">{selectedRecord.follow_up_notes}</p>
                  )}
                </div>
              )}

              {/* Meta */}
              <div className="text-xs text-slate-500 border-t pt-4">
                Created by {selectedRecord.created_by_name} on {selectedRecord.created_at ? format(parseISO(selectedRecord.created_at), 'PPP p') : 'N/A'}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button onClick={() => { setShowViewDialog(false); openEditDialog(selectedRecord); }} className="bg-[#1e3a5f]">
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Bell className="w-5 h-5" />
              Set Meeting Reminder
            </DialogTitle>
          </DialogHeader>
          
          {reminderRecord && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{reminderRecord.title}</p>
                <p className="text-sm text-slate-500">
                  {reminderRecord.date} at {reminderRecord.start_time}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Remind me before</Label>
                <Select value={reminderMinutes} onValueChange={setReminderMinutes}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="1440">1 day</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <p className="text-sm text-slate-500">
                You'll receive an email reminder {reminderMinutes === '1440' ? '1 day' : `${reminderMinutes} minutes`} before the meeting.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderDialog(false)}>Cancel</Button>
            <Button onClick={setReminder} className="bg-purple-600 hover:bg-purple-700">
              <Bell className="w-4 h-4 mr-2" />
              Set Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
